import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import styled, { css } from 'styled-components';
import { Text } from '@/components/Text';
import { Constrain } from './Constrain';
import etchoLogoBlue from '@/images/etcho-logo-blue.svg';
import { Breakpoints } from '@/styles/breakpoints';
import { useOptionalUser } from '@/hooks/useOptionalUser';
import { useTenantConfig } from '@/tenant/TenantConfigProvider';
import { tenantLogoUrl } from '@/tenant/tenantBranding';

type MastheadProps = {
    $noMargin?: boolean;
};

export function Masthead({ $noMargin }: MastheadProps): JSX.Element {
    const user = useOptionalUser();
    const { logout } = useAuth0();
    const tenantConfig = useTenantConfig();
    const logoUrl = tenantLogoUrl(tenantConfig);

    const brandMark = logoUrl ? (
        <img src={logoUrl} alt={tenantConfig?.name ?? ''} />
    ) : (
        <img src={etchoLogoBlue} alt="The Etcho logo in blue and orange" />
    );

    return (
        <MastheadStyles $noMargin={$noMargin}>
            <Constrain>
                <MastheadLayout>
                    {user !== undefined && (
                        <BrandingContainer
                            as={Link}
                            to={'/'}
                            $branded={!!logoUrl}
                        >
                            {brandMark}
                        </BrandingContainer>
                    )}

                    {user === undefined && (
                        <BrandingContainer as="div" $branded={!!logoUrl}>
                            {brandMark}
                        </BrandingContainer>
                    )}

                    {user !== undefined && (
                        <Navigation>
                            <ul>
                                <li>
                                    <Link to={'/'}>
                                        <Text color="uNavy" size="small">
                                            Dashboard
                                        </Text>
                                    </Link>
                                </li>
                                <li>
                                    <Link to={'/settings/user'}>
                                        <Text color="uNavy" size="small">
                                            Settings
                                        </Text>
                                    </Link>
                                </li>
                                <li>
                                    <Text
                                        as="button"
                                        color="uNavy"
                                        size="small"
                                        onClick={() =>
                                            logout({
                                                logoutParams: {
                                                    returnTo:
                                                        window.location.origin,
                                                },
                                            })
                                        }
                                    >
                                        Log out
                                    </Text>
                                </li>
                            </ul>
                        </Navigation>
                    )}
                </MastheadLayout>
            </Constrain>
        </MastheadStyles>
    );
}

/***
 * Styles
 */
const MastheadStyles = styled.div<MastheadProps>`
    padding: 12px 0;
    background-color: ${({ theme }) => theme.colors.white};
    position: sticky;
    top: 0;
    // Sits above page content but deliberately *below* modal routes
    // (ModalRouteShell is z-index 1000). A modal's backdrop then covers the
    // Masthead — it stays pinned in place but dimmed and non-interactive —
    // rather than the two tying at 1000 and fighting over paint order.
    z-index: 900;

    ${({ $noMargin }) =>
        !$noMargin &&
        css`
            margin-bottom: 20px;

            @media (min-width: ${Breakpoints.Medium}) {
                margin-bottom: 40px;
            }
        `}
`;

const MastheadLayout = styled.div`
    display: flex;
    flex-direction: row;
    gap: 32px;
    justify-content: space-between;
`;

const BrandingContainer = styled.div<{ $branded?: boolean }>`
    flex: 90px 0 0;
    display: flex;
    align-items: center;

    img {
        width: 100%;
    }

    // Tenant logos are arbitrary shapes (often wide wordmarks) — bound by
    // height instead of the fixed Etcho-mark width.
    ${({ $branded }) =>
        $branded &&
        css`
            flex: 0 0 auto;

            img {
                width: auto;
                height: 32px;
                max-width: 145px;
                object-fit: contain;
            }
        `}
`;

const Navigation = styled.nav`
    align-self: center;

    ul,
    li {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
    }

    ul {
        display: flex;
        gap: 20px;
        align-items: center;
        justify-content: center;
    }

    a {
        text-decoration: none;
    }
`;
